/* ==========================================================================
   Bedrock — Bandeau d'annonces (bdr-announcement-rotator)
   Rotation auto avec pause au survol/focus, respect de prefers-reduced-motion,
   fermeture mémorisée en sessionStorage.
   ========================================================================== */

(function () {
  'use strict';

  function reduceMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function sessionGet(key) {
    try {
      return window.sessionStorage.getItem(key);
    } catch (error) {
      return null;
    }
  }

  function sessionSet(key, value) {
    try {
      window.sessionStorage.setItem(key, value);
    } catch (error) {
      /* stockage indisponible (navigation privée, quota…) : on ignore silencieusement */
    }
  }

  class BdrAnnouncementRotator extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-bdr-track]');
      this.viewport = this.querySelector('[data-bdr-viewport]');
      if (!this.track || !this.viewport) return;

      this.slides = Array.from(this.track.querySelectorAll('[data-bdr-slide]'));
      this.prevBtn = this.querySelector('[data-bdr-prev]');
      this.nextBtn = this.querySelector('[data-bdr-next]');
      this.closeBtn = this.querySelector('[data-bdr-close]');
      this.storageKey = this.dataset.bdrStorageKey || '';
      this.rotationMs = parseInt(this.dataset.bdrRotationMs, 10) || 5000;
      this.transition = this.dataset.bdrTransition || 'fade';
      this.index = 0;
      this.timer = null;
      this.paused = false;

      if (this.storageKey && sessionGet(this.storageKey) === 'closed') {
        this.hidden = true;
        return;
      }

      this.slides.forEach((slide) => slide.removeAttribute('hidden'));
      this.applyIndex(0);

      if (this.prevBtn) {
        this.onPrev = () => this.step(-1);
        this.prevBtn.addEventListener('click', this.onPrev);
      }
      if (this.nextBtn) {
        this.onNext = () => this.step(1);
        this.nextBtn.addEventListener('click', this.onNext);
      }
      if (this.closeBtn) {
        this.onClose = () => this.close();
        this.closeBtn.addEventListener('click', this.onClose);
      }

      this.onPause = () => this.pause();
      this.onResume = () => this.resume();
      this.addEventListener('mouseenter', this.onPause);
      this.addEventListener('mouseleave', this.onResume);
      this.addEventListener('focusin', this.onPause);
      this.addEventListener('focusout', this.onResume);

      this.start();
    }

    disconnectedCallback() {
      this.stop();
      if (this.prevBtn && this.onPrev) this.prevBtn.removeEventListener('click', this.onPrev);
      if (this.nextBtn && this.onNext) this.nextBtn.removeEventListener('click', this.onNext);
      if (this.closeBtn && this.onClose) this.closeBtn.removeEventListener('click', this.onClose);
      this.removeEventListener('mouseenter', this.onPause);
      this.removeEventListener('mouseleave', this.onResume);
      this.removeEventListener('focusin', this.onPause);
      this.removeEventListener('focusout', this.onResume);
    }

    start() {
      if (this.slides.length < 2 || reduceMotion()) return;
      this.stop();
      this.timer = window.setInterval(() => this.step(1), this.rotationMs);
    }

    stop() {
      if (this.timer) {
        window.clearInterval(this.timer);
        this.timer = null;
      }
    }

    pause() {
      this.paused = true;
      this.stop();
    }

    resume() {
      this.paused = false;
      this.start();
    }

    step(direction) {
      if (!this.slides.length) return;
      const next = (this.index + direction + this.slides.length) % this.slides.length;
      this.applyIndex(next);
      if (!this.paused) this.start();
    }

    applyIndex(index) {
      this.index = index;

      this.slides.forEach((slide, i) => {
        const active = i === index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      });

      if (this.transition === 'slide') {
        this.track.style.setProperty('--bdr-rotator-index', String(index));
      }
    }

    close() {
      if (this.storageKey) sessionSet(this.storageKey, 'closed');
      this.stop();
      this.hidden = true;
    }
  }

  class BdrAnnouncementLocalization extends HTMLElement {
    connectedCallback() {
      this.selects = Array.from(this.querySelectorAll('[data-bdr-auto-submit]'));
      this.onChange = (event) => {
        const form = event.target.closest('form');
        if (form) form.submit();
      };
      this.selects.forEach((select) => select.addEventListener('change', this.onChange));
    }

    disconnectedCallback() {
      this.selects.forEach((select) => select.removeEventListener('change', this.onChange));
    }
  }

  class BdrLanguagePicker extends HTMLElement {
    connectedCallback() {
      this.toggle = this.querySelector('[data-bdr-toggle]');
      this.list = this.querySelector('.bdr-language-picker__list');
      if (!this.toggle || !this.list) return;

      this.onToggle = () => this.setOpen(this.list.hidden);
      this.onDocumentClick = (event) => {
        if (!this.contains(event.target)) this.setOpen(false);
      };
      this.onKeydown = (event) => {
        if (event.key === 'Escape' && !this.list.hidden) {
          this.setOpen(false);
          this.toggle.focus();
        }
      };

      this.toggle.addEventListener('click', this.onToggle);
      document.addEventListener('click', this.onDocumentClick);
      this.addEventListener('keydown', this.onKeydown);
    }

    disconnectedCallback() {
      if (this.toggle && this.onToggle) this.toggle.removeEventListener('click', this.onToggle);
      if (this.onDocumentClick) document.removeEventListener('click', this.onDocumentClick);
      if (this.onKeydown) this.removeEventListener('keydown', this.onKeydown);
    }

    setOpen(open) {
      this.list.hidden = !open;
      this.toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    }
  }

  if (!customElements.get('bdr-announcement-rotator')) {
    customElements.define('bdr-announcement-rotator', BdrAnnouncementRotator);
  }
  if (!customElements.get('bdr-announcement-localization')) {
    customElements.define('bdr-announcement-localization', BdrAnnouncementLocalization);
  }
  if (!customElements.get('bdr-language-picker')) {
    customElements.define('bdr-language-picker', BdrLanguagePicker);
  }
})();
